import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  CheckMark,
  ErrorModal,
  getPaymentRequired,
  getTranslation,
  Loader,
  PartnershipBranding,
  PoweredBy,
  ReservationSummary,
  Spacer,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';

import inFrame from '../../utils/inFrame';

import BookingForm from '../BookingForm';
import getOrderFromReservationDetails from './getOrderFromReservationDetails';
import GlobalMessage from '../GlobalMessage';
import OnScrollView from '../OnScrollView';
import OrderSummary from '../OrderSummary';
import PaymentMethodDropdown from '../PaymentMethodDropdown';
import reservationDetailsShape, { options as serviceChargeOptionsShape } from './reservationDetailsShape';
import SummaryLegalText from './SummaryLegalText';
import SummarySection, {
  SummarySectionBody,
  SummarySectionHeader,
} from '../SummarySection';
import VenueMarketingOptIn from '../VenueMarketingOptIn';
import WidgetNav from '../WidgetNav';
import WidgetPage, {
  WidgetPageBody,
  WidgetPageFooter,
  WidgetPageHeader,
} from '../WidgetPage';
import WidgetTitle from '../WidgetTitle';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { selectIsConcierge } from '../../../../../ui-kitchen/src/reducers/userSlice';
import ResyCreditBanner from '../../../../../ui-kitchen/src/components/ResyCreditBanner';
import { selectExperiment } from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { IS_JCE_BRIDGE_ENABLED, RESY_CREDIT_TEMP_COPY } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';
import './SummaryPage.scss';
import { PENDING } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import isGrnVenue from '../../utils/isGrnVenue';
import checkIfResyWebUrl from '../../../../../ui-kitchen/src/utils/checkIfResyWebUrl';
import { setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { isUSOpenVenueId } from '../../../../../ui-kitchen/src/utils/isUSOpenVenue';
import OPENAI_WIDGET from '../../constants/srcTypes';

const summaryPagePropTypes = {
  affId: PropTypes.string,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  backPath: PropTypes.string,
  // redux booking state
  bookingState: PropTypes.shape({
    bookOnBehalfOf: PropTypes.bool,
    bookOnBehalfOfPayload: PropTypes.shape({
      email: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      mobile_number: PropTypes.string,
    }),
    canBook: PropTypes.bool,
    nonce: PropTypes.string,
  }).isRequired,
  canBookOnBehalf: PropTypes.bool,
  clearError: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.shape({}),
        ctaTextKey: PropTypes.string,
        message: PropTypes.string,
        onClick: PropTypes.func,
        status: PropTypes.string,
      }),
    ])
  ),
  experiments: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isBookingMetadataInited: PropTypes.bool.isRequired,
  isConcierge: PropTypes.bool.isRequired,
  legalText: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  navSearch: PropTypes.shape({
    date: PropTypes.string,
    seats: PropTypes.number,
  }).isRequired,
  offerId: PropTypes.string,
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  pending: PropTypes.bool,
  preferredLanguage: PropTypes.string,
  previewMode: PropTypes.bool.isRequired,
  reservationDetails: reservationDetailsShape,
  reservationInfo: PropTypes.shape({
    colors: PropTypes.shape({
      background: PropTypes.string,
      font: PropTypes.string,
    }),
    event: PropTypes.shape({
      title: PropTypes.string,
    }),
    partySize: PropTypes.number,
  }),
  selectedPaymentMethod: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  serviceChargeOptions: serviceChargeOptionsShape,
  specs: PropTypes.shape({
    day: PropTypes.string,
    time: PropTypes.string,
  }),
  sourceId: PropTypes.string,
  utmSource: PropTypes.string,
  partnerId: PropTypes.string,
  thunks: PropTypes.shape({
    attemptBook: PropTypes.func,
    setBookingFlowState: PropTypes.func,
    bookingFlowSummaryAdvance: PropTypes.func,
    postBookingBookThunk: PropTypes.func,
    commitDetailsThunk: PropTypes.func,
    setAffId: PropTypes.func,
    setBookOnBehalfOf: PropTypes.func,
    setBookOnBehalfOfPayload: PropTypes.func,
    setOfferId: PropTypes.func,
    setPaymentId: PropTypes.func,
    setReplace: PropTypes.func,
    setResyToken: PropTypes.func,
    setSourceId: PropTypes.func,
    setUtmSource: PropTypes.func,
    setPartnerId: PropTypes.func,
    setServiceCharge: PropTypes.func,
  }).isRequired,
  toggleVenueCommunicationOptIn: PropTypes.func.isRequired,
  venueCommunicationOptIn: PropTypes.bool,
  venueInfo: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    isResyCreditEligible: PropTypes.bool,
    source: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
    }),
  }),
  viewPortHeight: PropTypes.number,
};

class SummaryPage extends React.Component {
  isLoadedTimer = null;

  state = {
    footerShadow: true,
    /* eslint-disable-next-line react/no-unused-state */
    items: undefined,
    isLoaded: false,
    // for analytics
    presentBookOnBehalfOfError: false,
    presentCancel: false,
    showDoubleConfirm: false,
    titleOpacity: 0,
  }

  static propTypes = summaryPagePropTypes;

  static defaultProps = {
    affId: undefined,
    backPath: undefined,
    canBookOnBehalf: false,
    clearError: undefined,
    errors: undefined,
    legalText: '',
    offerId: undefined,
    paymentMethods: undefined,
    pending: false,
    preferredLanguage: 'en-US',
    reservationDetails: undefined,
    reservationInfo: undefined,
    selectedPaymentMethod: undefined,
    serviceChargeOptions: undefined,
    specs: undefined,
    sourceId: undefined,
    utmSource: undefined,
    partnerId: undefined,
    venueCommunicationOptIn: false,
    venueInfo: undefined,
    viewPortHeight: undefined,
  }

  componentDidMount() {
    this.setBookingMetadataFromRouterState();
    this.handleIsLoaded();
  }

  componentDidUpdate(prevProps) {
    const { isBookingMetadataInited } = this.props;
    if (prevProps.isBookingMetadataInited !== isBookingMetadataInited) {
      this.setBookingMetadataFromRouterState();
    }
  }

  componentWillUnmount() {
    if (this.isLoadedTimer) {
      window.clearTimeout(this.isLoadedTimer);
      this.isLoadedTimer = null;
    }
  }

  handleIsLoaded = () => {
    this.isLoadedTimer = window.setTimeout(() => {
      this.setState({ isLoaded: true });
    }, 500);
  }

  getButtonStyles = () => {
    const {
      reservationInfo: {
        colors,
      } = {},
    } = this.props;

    if (!colors) {
      return null;
    }

    return { colors };
  }

  getContextForNavState = () => {
    const {
      isAuthenticated,
      reservationDetails: {
        hasMenus,
        templateId,
      } = {},
      venueInfo: {
        id: venueId,
      },
    } = this.props;

    return {
      isUserAuthenticated: isAuthenticated,
      templateId: hasMenus && templateId,
      venueId,
    };
  }

  isEvent = () => {
    const {
      reservationInfo: {
        event,
      } = {},
    } = this.props;

    return !!event;
  }

  onAddNewPaymentMethod = () => {
    const { navigate } = this.props;

    navigate('/add-payment');
    setSearchParams('next', '/reservation-details');
  }

  onCancel = () => {
    this.setState({ presentCancel: true });
  }

  // These Booking metadata values are captured at Widget load-time in resyRouter slice,
  // and passed along to booking slice ahead of booking-time.
  setBookingMetadataFromRouterState = () => {
    const {
      affId,
      offerId,
      sourceId,
      utmSource,
      partnerId,
      isBookingMetadataInited,
      thunks: {
        setAffId,
        setOfferId,
        setSourceId,
        setUtmSource,
        setPartnerId,
      } = {},
    } = this.props;
    if (isBookingMetadataInited) {
      if (affId) {
        setAffId(affId);
      }
      if (offerId) {
        setOfferId(offerId);
      }
      if (sourceId) {
        setSourceId(sourceId);
      }
      if (utmSource) {
        setUtmSource(utmSource);
      }
      if (partnerId) {
        setPartnerId(partnerId);
      }

      this.scrollToTopForOpenAiWidget();
    }
  }

  scrollToTopForOpenAiWidget = () => {
    const { sourceId } = this.props;

    if (sourceId !== OPENAI_WIDGET) {
      return;
    }

    const scrollContainer = document.querySelector('.SummaryPage__ScrollView');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
    window?.scrollTo(0, 0);
  }

  scrollFunc = (elems, scrollTop) => {
    const diffWithScrollTop = (elems[0].offsetTop + elems[0].offsetHeight) - scrollTop;
    const perc = 1 - diffWithScrollTop / elems[0].offsetHeight;

    this.setState({
      titleOpacity: (perc <= 0) ? 0 : 1,
    });
  }

  scrollFuncFooter = (elems, scrollTop) => {
    this.setState({
      footerShadow: !((scrollTop + elems[0].clientHeight) >= (elems[0].scrollHeight - 60)),
    });
  }

  getScrollEvents = () => [
    {
      selector: ['.ReservationSummary__lock h2'],
      func: this.scrollFunc,
    },
    {
      selector: ['.SummaryPage__ScrollView'],
      func: this.scrollFuncFooter,
    },
  ]

  isLoading = () => {
    const { pending, reservationDetails } = this.props;
    return pending || !reservationDetails;
  }

  closeBookOnBehalfOfError = () => {
    this.setState({ presentBookOnBehalfOfError: undefined });
  }

  validateBookOnBehalfOf = (payload) => {
    const errors = Object.keys(payload).filter((key) => !payload[key]);

    const getMissingKeys = (names) => {
      const KEY_MAP = {
        first_name: 'first_name',
        last_name: 'last_name',
        email: 'email',
        phone: 'mobile_phone',
      };

      const translatedKeys = names.map((name) => getTranslation({ translationKey: KEY_MAP[name], fallback: name }));
      // TODO: Use locale-aware list formatting (e.g. Intl.ListFormat) instead of join(', ') for i18n correctness.
      // See ReservationQuestionnaire.jsx for sample usage.
      return translatedKeys.join(', ');
    };

    if (errors.length) {
      return {
        code: 'book_on_behalf',
        serverError: true,
        msg: getTranslation({
          translationKey: 'enter_missing_info',
          translationValues: { missingFields: getMissingKeys(errors) },
          fallback: `Please enter the following guest information for the reservation:\r${getMissingKeys(errors)}`,
        }),
        keys: errors,
        onClick: this.closeBookOnBehalfOfError,
      };
    }

    return undefined;
  }

  onBook = () => {
    const {
      showDoubleConfirm,
    } = this.state;

    const {
      bookingState: {
        bookOnBehalfOf,
        bookOnBehalfOfPayload,
      },
      errors,
      isAuthenticated,
      paymentMethods,
      thunks: {
        attemptBook,
      } = {},
      reservationDetails: {
        config: {
          double_confirmation: doubleConfirmation,
        } = {},
      } = {},
    } = this.props;

    if (bookOnBehalfOf) {
      const bookOnBehalfOfError = this.validateBookOnBehalfOf(bookOnBehalfOfPayload);
      if (bookOnBehalfOfError) {
        this.setState({ bookOnBehalfOfError, presentBookOnBehalfOfError: true });
        return;
      }
      this.setState({ bookOnBehalfOfError, presentBookOnBehalfOfError: false });
    }

    // requires double confirm and it is not currently being shown
    if (doubleConfirmation && !showDoubleConfirm) {
      this.setState({
        showDoubleConfirm: true,
      });
      return;
    }

    // reset showDoubleConfirm if it is being shown
    // booking will happen
    if (showDoubleConfirm) {
      this.setState({
        showDoubleConfirm: false,
      });
    }

    // 303 errors provide a url to which the book CTA redirects when clicked
    if (errors?.[0]?.response?.status === 303) {
      window.top.location.assign(errors?.[0]?.response?.data?.data?.web);
      return;
    }

    attemptBook({
      isAuthenticated,
      cardsOnFile: !!paymentMethods?.length,
    });
  }

  onCloseModal = () => {
    const { clearError } = this.props;

    if (clearError) {
      clearError();
    }
    this.setState({
      presentCancel: false,
    });
  }

  onConfirmCancelAndBook = () => {
    const {
      isAuthenticated,
      thunks: {
        attemptBook,
        setReplace,
      } = {},
      paymentMethods,
    } = this.props;

    setReplace(true);
    // pass in 1 as value for 'replace'
    attemptBook({
      isAuthenticated,
      cardsOnFile: !!paymentMethods?.length,
    });
  }

  onPaymentMethodSelect = (response) => {
    const {
      thunks: {
        setPaymentId,
      },
    } = this.props;
    const {
      currentTarget: {
        value,
      },
    } = response;

    setPaymentId(+value);
  }

  onServiceChargeChange = (val) => {
    const {
      thunks: {
        commitDetailsThunk,
        setServiceCharge,
      } = {},
    } = this.props;
    const { value } = val;

    // value is going to be a string
    // eg '0%' || '15%' || '25%'
    if (value) {
      setServiceCharge(value);
      // update the reservation on the server
      commitDetailsThunk();
    }
  }

  renderAbout = () => {
    if (this.isEvent()) {
      return null;
    }

    const {
      reservationDetails: {
        venue: {
          content,
        } = {},
      } = {},
    } = this.props;

    const about = content?.find((i) => i.name === 'about');
    if (!about?.body) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="about_header">
              <span>About</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            {about.body}
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderAddCard = () => {
    const {
      bookingState: {
        nonce,
      } = {},
    } = this.props;

    return (
      <TranslationProvider translationKey={nonce ? 'credit_card_change_label' : 'credit_card_add_label'}>
        <Button
          className="AddCard"
          data-test-id="order_summary_page-button-add_card"
          onClick={this.onAddNewPaymentMethod}
          variant="link"
        >
          {nonce ? 'Change card' : 'Add new card'}
        </Button>
      </TranslationProvider>
    );
  }

  renderBook = () => {
    const {
      showDoubleConfirm,
    } = this.state;
    const {
      bookingState: {
        canBook,
      } = {},
      reservationInfo: {
        isGda,
      } = {},
    } = this.props;

    let key;
    let str;
    if (showDoubleConfirm) {
      key = 'button_confirm';
      str = 'Confirm';
    } else {
      key = 'reserve_now';
      str = 'Reserve Now';
    }

    const colors = (!isGda && this.getButtonStyles()) || null;

    return (
      <div className="SummaryPage__book">
        <Button
          data-test-id="order_summary_page-button-book"
          disabled={!canBook || this.isLoading()}
          lg
          onClick={this.onBook}
          {...colors}
          variant={utils.truthyKey({
            'double-confirm': showDoubleConfirm,
            rga: isGda,
          }) || 'primary'}
        >
          <TranslationProvider translationKey={key}>
            <span>{str}</span>
          </TranslationProvider>
        </Button>
      </div>
    );
  }

  renderCancellationPolicy = () => {
    const {
      reservationDetails: {
        cancellation: {
          display: {
            policy,
          } = {},
        } = {},
      } = {},
    } = this.props;

    if (policy && policy.length) {
      return (
        <>
          <SummarySection>
            <SummarySectionHeader>
              <TranslationProvider translationKey="cancellation_policy_header">
                <span>Cancellation Policy</span>
              </TranslationProvider>
            </SummarySectionHeader>
            <SummarySectionBody className="SummaryPage__cancellation">
              <ul>
                {policy.map((p) => <li key={p.substring(0, 9)}>{p}</li>)}
              </ul>
            </SummarySectionBody>
          </SummarySection>
          <Spacer height="30" />
        </>
      );
    }

    return null;
  }

  renderResyCreditBanner = () => {
    const {
      experiments,
      venueInfo: {
        isResyCreditEligible,
      } = {},
    } = this.props;
    const { enabled: isResyCreditBannerEnabled } = selectExperiment(
      { experiments },
      IS_JCE_BRIDGE_ENABLED
    );
    const { enabled: isResyCreditTempCopyEnabled } = selectExperiment(
      { experiments },
      RESY_CREDIT_TEMP_COPY
    );

    if (!isResyCreditBannerEnabled || !isResyCreditEligible) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <span>
              <TranslationProvider translationKey="resy_credit_banner_header_eligible">
                <span>Qualifies for Resy Credit</span>
              </TranslationProvider>
              {' '}
              <CheckMark className="SummaryPage__resy-credit-checkmark" />
            </span>
          </SummarySectionHeader>
          <SummarySectionBody>
            <ResyCreditBanner hideTitle useTempCopy={isResyCreditTempCopyEnabled} />
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderCommunicationOptIn = () => {
    const {
      bookingState: {
        bookOnBehalfOf,
      } = {}, toggleVenueCommunicationOptIn, venueCommunicationOptIn, venueInfo,
    } = this.props;

    if (!venueInfo || (venueInfo && isGrnVenue(venueInfo?.source)) || bookOnBehalfOf) {
      return null;
    }

    return (
      <>
        <VenueMarketingOptIn
          checked={venueCommunicationOptIn}
          // invoking toggleVenueCommunicationOptIn with no arguments to avoid implicitly passing a synthetic event payload to a thunk because it's not serializable
          onChange={() => toggleVenueCommunicationOptIn()}
          venueInfo={venueInfo}
        />
        <Spacer height="30" />
      </>
    );
  }

  bookOnBehalfOfPayloadChange = (response) => {
    // TODO(CON-17456): Normalize concierge phone digits and enforce shared
    // phoneValidation utility checks when this flow is prioritized.
    const {
      thunks: {
        setBookOnBehalfOfPayload,
      } = {},
    } = this.props;

    const {
      // eslint-disable-next-line camelcase
      country_code,
      // eslint-disable-next-line camelcase
      first_name,
      // eslint-disable-next-line camelcase
      last_name,
      // eslint-disable-next-line camelcase
      email,
      phone,
    } = response;

    setBookOnBehalfOfPayload({
      country_code,
      first_name,
      last_name,
      email,
      // eslint-disable-next-line camelcase
      mobile_number: `+${country_code}${phone}`,
      phone,
    });
  }

  toggleBookOnBehalfOf = (val) => {
    const {
      thunks: {
        setBookOnBehalfOf,
      } = {},
    } = this.props;
    setBookOnBehalfOf(val);
  }

  renderConciergeBooking = () => {
    const { canBookOnBehalf } = this.props;
    if (!canBookOnBehalf) {
      return null;
    }

    const { bookOnBehalfOfError } = this.state;
    const {
      bookingState: {
        bookOnBehalfOf: selected,
        bookOnBehalfOfPayload,
      } = {},
    } = this.props;

    return (
      <>
        <SummarySection>
          <SummarySectionHeader id="concierge_section">
            <TranslationProvider translationKey="concierge_booking">
              <span>Concierge Booking</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            <BookingForm
              bookOnBehalfOf={selected}
              error={bookOnBehalfOfError}
              info={bookOnBehalfOfPayload}
              onBookingTypeChange={this.toggleBookOnBehalfOf}
              onBookingInfoChange={this.bookOnBehalfOfPayloadChange}
            />
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  // double confirm can come from
  // the reservation or
  // a 303 error (redirect)
  renderDoubleConfirm = () => {
    const { showDoubleConfirm } = this.state;
    const {
      errors,
      reservationDetails: {
        config: {
          double_confirmation: doubleConfirmMessages,
        } = {},
      } = {},
    } = this.props;

    let message303;

    if (errors?.[0]?.response?.status === 303) {
      message303 = errors?.[0]?.response?.data?.message;
    }

    // if there's a 303 message we always show
    // showDoubleConfirm state is used for typical case:
    // venue has configured a double confirm message
    if (!message303 && (!showDoubleConfirm || !doubleConfirmMessages)) {
      return null;
    }

    return (
      <GlobalMessage
        position={{ bottom: '80px', left: '0' }}
        messages={message303 || doubleConfirmMessages}
        ariaLabel={getTranslation({ translationKey: 'close_double_confirmation_booking_message', fallback: 'Close double confirmation booking message' })}
      />
    );
  }

  renderIncludedWithTicket = () => {
    const {
      reservationInfo: {
        whatsIncluded,
      } = {},
    } = this.props;

    if (!whatsIncluded || !whatsIncluded.length) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="included_with_ticket">
              <span>Included With This Ticket</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            <ul className="SummaryPage__MenuHighlights">
              {
                whatsIncluded.map(
                  (val) => <li key={val}>{val}</li>
                )
              }
            </ul>
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderLoader = () => {
    if (!this.isLoading()) {
      return null;
    }
    return <Loader />;
  }

  renderMenuHighlights = () => {
    const {
      reservationDetails: {
        config: {
          menu_items: menuItems,
        } = {},
      } = {},
    } = this.props;

    if (!menuItems || !menuItems?.length) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="menu_highlights">
              <span>Menu Highlights</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            <ul className="SummaryPage__MenuHighlights">
              {
                menuItems.map(
                  (val) => <li key={val}>{val}</li>
                )
              }
            </ul>
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderEditReservation = (error) => {
    const ctaTextKey = 'edit_reservation';
    const ctaText = 'Edit Reservation';
    const onClick = error?.onClick || this.onCloseModal;

    const {
      venueInfo: {
        id: venueId,
      },
    } = this.props;

    const isUSOpenVenue = isUSOpenVenueId(venueId);

    return (
      <ErrorModal
        message={error.message}
        onErrorModalClose={this.onCloseModal}
        primaryCTAKey={ctaTextKey}
        primaryCTAOnClick={onClick}
        primaryCTAText={!isUSOpenVenue ? ctaText : null}
        primaryCTAVariant="primary"
      />
    );
  };

  renderExistingReservation = (error) => {
    const {
      data: {
        tableType,
        venue: {
          name: venueName,
        },
      } = {},
    } = error;

    const message = (
      <TranslationProvider
        translationKey="cancel_existing_reservation_message"
        translationValues={{
          serviceType: tableType,
          venueName,
        }}
      >
        <div role="alert">
          {`You already have a <b>${tableType}</b> reservation at <b>${venueName}</b>. Do you want to cancel that reservation and continue booking?`}
        </div>
      </TranslationProvider>
    );
    const onClick = error?.onClick || this.onCloseModal;
    return (
      <ErrorModal
        message={message}
        onErrorModalClose={this.onCloseModal}
        primaryCTAKey="abort_cancel_existing_reservation"
        primaryCTAOnClick={onClick}
        primaryCTAText="Keep Existing Reservation"
        primaryCTAVariant="primary"
        secondaryCTAKey="continue_button"
        secondaryCTAOnClick={this.onConfirmCancelAndBook}
        secondaryCTAText="Continue Booking"
        secondaryCTAVariant="secondary"
      />
    );
  }

  renderModal = () => {
    const { bookOnBehalfOfError, presentBookOnBehalfOfError, presentCancel } = this.state;
    const { errors } = this.props;

    const error = errors?.[0] || bookOnBehalfOfError;

    if (!errors?.length && !presentBookOnBehalfOfError && !presentCancel) {
      return null;
    }

    if (!errors?.length && presentBookOnBehalfOfError && !presentCancel) {
      return null;
    }

    if (presentCancel) {
      return (
        (error?.status === 412 && error?.code !== 1019)
          ? this.renderExistingReservation(error)
          : this.renderEditReservation(error)
      );
    }

    return (
      (error?.status === 412 && error?.code !== 1019)
        ? this.renderExistingReservation(error)
        : this.renderEditReservation(error)
    );
  }

  renderNeedToKnow = () => {
    const {
      reservationInfo: {
        event: {
          needToKnowCopy,
        } = {},
      } = {},
    } = this.props;

    if (!needToKnowCopy) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="need_to_know_header">
              <span>Need to Know</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            {needToKnowCopy}
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderNoAvailability = () => {
    const {
      bookingState: {
        canBook,
      } = {},
      specs: {
        partySize,
      } = {},
    } = this.props;

    if (canBook) {
      return null;
    }

    return (
      <div>
        <TranslationProvider translationKey="no_availability_for_party_message" translationValues={{ partySize }}>
          <span>{`Sorry, we don't currently have any tables available for ${partySize}.`}</span>
        </TranslationProvider>
        <Spacer height="30" />
      </div>
    );
  }

  renderOrderSummary = () => {
    const {
      bookingState: {
        canBook,
      } = {},
      preferredLanguage,
      reservationDetails,
      serviceChargeOptions,
      specs: {
        tableType,
      } = {},
      venueInfo: {
        locale: {
          currency: currencyCode,
        } = {},
        isUSLocation,
        taxIncluded,
      } = {},
    } = this.props;

    if (!canBook) {
      return null;
    }

    if (!reservationDetails) {
      return null;
    }

    const order = {
      ...getOrderFromReservationDetails(reservationDetails, serviceChargeOptions),
      currencyCode,
      isUSLocation,
      locale: preferredLanguage,
      reservationItemName: tableType,
      serviceChargeChange: this.onServiceChargeChange,
      taxIncluded,
    };

    if (!order.total) {
      return null;
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="order_summary">
              <span>Order Summary</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody>
            <OrderSummary {...order} />
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderPartnershipBranding = () => {
    const {
      venueInfo: {
        source,
      } = {},
    } = this.props;

    const iFrameParentUrl = (window.location !== window.parent.location) ? document.referrer : document.location.href;
    const isExternal = !checkIfResyWebUrl(iFrameParentUrl);
    const { name, logo } = source || {};
    const grnVenue = isGrnVenue(source);

    return isExternal ? (
      <>
        <PoweredBy
          hideCopy={grnVenue}
        />
        {grnVenue
          && (
            <PartnershipBranding
              alt={name}
              image={logo}
            />
          )}
      </>
    ) : null;
  }

  renderPaymentMethod = () => {
    const {
      isAuthenticated,
      isConcierge,
      paymentMethods,
      reservationDetails,
    } = this.props;
    const hasNoPaymentMethods = !paymentMethods?.length;
    if (
      !getPaymentRequired(reservationDetails)
      || !isAuthenticated
      || (!isConcierge && hasNoPaymentMethods)
    ) {
      return null;
    }

    let content;

    const {
      bookingState: {
        bookOnBehalfOf,
        nonce,
        paymentId,
      } = {},
    } = this.props;

    if (bookOnBehalfOf) {
      content = (
        <>
          {(!!nonce) && <div className="SummaryPage__payment-obscured">&#8226;&#8226;&#8226;&#8226;</div>}
        </>
      );
    } else {
      content = hasNoPaymentMethods ? null : (
        <PaymentMethodDropdown
          addNew={this.onAddNewPaymentMethod}
          bookOnBehalfOf={bookOnBehalfOf}
          disabled={this.isLoading()}
          onClick={this.onPaymentMethodClick}
          onPaymentMethodSelect={this.onPaymentMethodSelect}
          options={paymentMethods}
          selectedPaymentMethod={paymentId}
        />
      );
    }

    return (
      <>
        <SummarySection>
          <SummarySectionHeader>
            <TranslationProvider translationKey="payment_method_header">
              <span>Payment Method</span>
            </TranslationProvider>
          </SummarySectionHeader>
          <SummarySectionBody className="SummaryPage__payment">
            {content}
            {this.renderAddCard()}
          </SummarySectionBody>
        </SummarySection>
        <Spacer height="30" />
      </>
    );
  }

  renderReservationSummary = () => {
    const {
      reservationDetails: {
        payment: {
          amounts: {
            quantity,
            // eslint-disable-next-line camelcase
            reservation_charge,
          } = {},
          config: {
            type: pricingType,
          } = {},
        } = {},
      } = {},
      reservationInfo: {
        event: {
          title,
        } = {},
        isGda,
      } = {},
      specs: {
        day,
        partySize,
        tableType,
        time,
      } = {},
      venueInfo: {
        locale: {
          currency: currencyCode,
        } = {},
        name,
      } = {},
    } = this.props;

    // eslint-disable-next-line camelcase
    const price = (pricingType === 'free') ? undefined : reservation_charge / quantity;

    const location = (title) ? name : undefined;

    return (
      <ReservationSummary
        currencyCode={currencyCode}
        eventName={title}
        location={location}
        isGda={isGda}
        price={price}
        seats={partySize}
        tableType={tableType}
        timeslot={{ day, time }}
        venue={name}
      />
    );
  }

  renderWidgetNav = () => {
    const {
      Analytics,
      backPath,
      navigate,
      navSearch,
      venueInfo: {
        name,
      } = {},
    } = this.props;

    const { titleOpacity } = this.state;

    return (
      <WidgetNav
        Analytics={Analytics}
        backPath={backPath}
        navigate={navigate}
        pageContext="reservation details"
        showBack
        showProfileMenu
        search={navSearch}
        title={name}
        titleOpacity={titleOpacity}
        venueName={name}
      />
    );
  }

  renderWidgetTitle = () => (
    <WidgetTitle>
      <TranslationProvider translationKey="complete_your_reservation">
        <span>Complete Your Reservation</span>
      </TranslationProvider>
    </WidgetTitle>
  );

  render() {
    const {
      footerShadow,
      isLoaded,
    } = this.state;

    const {
      viewPortHeight, previewMode, legalText, sourceId,
    } = this.props;

    return (
      <WidgetPage className="SummaryPage" height={inFrame() && viewPortHeight} ariaLabel={getTranslation({ translationKey: 'complete_your_reservation', fallback: 'Complete Your Reservation' })}>
        <div className="hidden-accessible" role="status" aria-live="polite" aria-atomic="true">
          {isLoaded && (
            <>
              {this.renderWidgetTitle()}
              {this.renderReservationSummary()}
            </>
          )}
        </div>
        {this.renderDoubleConfirm()}
        {this.renderModal()}
        {this.renderWidgetNav()}
        <OnScrollView className="SummaryPage__ScrollView" scrollEvents={this.getScrollEvents()}>
          {sourceId !== OPENAI_WIDGET && (<Spacer height="60" />)}
          <WidgetPageHeader>
            {this.renderWidgetTitle()}
            {this.renderReservationSummary()}
          </WidgetPageHeader>

          <WidgetPageBody>
            {this.renderLoader()}

            {this.renderNoAvailability()}

            {this.renderMenuHighlights()}

            {this.renderOrderSummary()}

            {this.renderConciergeBooking()}

            {this.renderPaymentMethod()}

            {this.renderCancellationPolicy()}

            {this.renderResyCreditBanner()}

            {this.renderNeedToKnow()}

            {this.renderAbout()}

            {this.renderIncludedWithTicket()}

            {this.renderCommunicationOptIn()}

            <hr />

            <SummaryLegalText legalText={legalText} sourceId={sourceId} />
            <Spacer height="20" />
            {this.renderPartnershipBranding()}
            {sourceId === OPENAI_WIDGET && <Spacer height="80" />}
          </WidgetPageBody>

          <WidgetPageFooter shadow={footerShadow} previewMode={previewMode}>
            {this.renderBook()}
          </WidgetPageFooter>

        </OnScrollView>
      </WidgetPage>
    );
  }
}

const mapStateToProps = (state) => {
  const { resyRouter } = state;
  const {
    search, previewMode, affId, offerId, sourceId, utmSource, partnerId, isBookingMetadataInited,
  } = resyRouter;
  return {
    experiments: state.experiments,
    isAuthenticated: selectIsAuthenticated(state),
    isConcierge: selectIsConcierge(state),
    navSearch: search,
    pending: state.booking.loadingDetails === PENDING || state.booking.loadingBooking === PENDING,
    previewMode,
    reservationDetails: state.booking?.detailsCommitted,
    venueCommunicationOptIn: state.booking?.venueCommunicationOptIn,
    affId,
    offerId,
    sourceId,
    utmSource,
    partnerId,
    isBookingMetadataInited,
  };
};

export { summaryPagePropTypes, SummaryPage as ComponentUnderTest };
export default connect(mapStateToProps)(SummaryPage);
